"use client";

import { useEffect, useMemo, useState } from "react";
import DeckImportForm from "@/components/DeckImportForm";
import MissingCardsPanel from "@/components/MissingCardsPanel";
import PrepSideEditor from "@/components/PrepSideEditor";
import SideGuidePanel from "@/components/SideGuidePanel";
import { migrateImportedPrepDecksToSaved } from "@/lib/deck-import";
import { baseLegendKey } from "@/lib/domains";
import {
  hydrateDeckEntries,
  listDecks,
  listInventory,
  listPrepPlans,
  savePrepPlan,
  deletePrepPlan,
  type InventoryEntry,
  type SavedDeck,
  type SavedDeckEntry,
  type PrepSidePlan,
  type SavedPrepPlan,
} from "@/lib/local-store";
import type { CardRecord } from "@/lib/types";

type PrepSelection = SavedDeckEntry[];

type DisplayEntry = {
  id: string;
  name: string;
  quantity: number;
  imageUrl?: string | null;
};

type PrepDeckSummary = {
  id: string;
  name: string;
  subtitle: string;
  imageUrl: string | null;
  totalCards: number;
  sideboardCount: number;
  battlefieldCount: number;
  mainEntries: DisplayEntry[];
  sideboardEntries: DisplayEntry[];
};

function emptySidePlan(): PrepSidePlan {
  return { sideIn: [], sideOut: [] };
}

function hydrateEntries(
  entries: SavedDeckEntry[],
  namesById: Map<string, string>,
  imagesById: Map<string, string | null>,
): DisplayEntry[] {
  return entries
    .filter((entry) => entry.quantity > 0)
    .map((entry) => ({
      id: entry.cardId,
      name: namesById.get(entry.cardId) || entry.cardId,
      quantity: entry.quantity,
      imageUrl: imagesById.get(entry.cardId) ?? null,
    }));
}

function CardThumb({ entry, qtyClass }: { entry: DisplayEntry; qtyClass: string }) {
  return (
    <div className="relative w-[4.25rem] shrink-0 overflow-hidden rounded-md border border-white/10 bg-black/40 sm:w-14 md:w-16">
      {entry.imageUrl ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={entry.imageUrl}
          alt={entry.name}
          className="aspect-[5/7] w-full object-cover"
          title={entry.name}
        />
      ) : (
        <div className="flex aspect-[5/7] items-center justify-center px-0.5 text-center text-[8px] leading-tight text-gray-500">
          {entry.name}
        </div>
      )}
      {entry.quantity > 1 ? (
        <span
          className={`absolute bottom-0.5 right-0.5 rounded px-1 py-0.5 text-[9px] font-bold tabular-nums shadow ${qtyClass}`}
        >
          x{entry.quantity}
        </span>
      ) : null}
    </div>
  );
}

function CardRow({
  label,
  labelClass,
  entries,
  emptyLabel,
  qtyClass,
}: {
  label: string;
  labelClass: string;
  entries: DisplayEntry[];
  emptyLabel: string;
  qtyClass: string;
}) {
  return (
    <div className="min-w-0">
      <p className={`mb-1 text-[10px] font-semibold uppercase tracking-wide ${labelClass}`}>{label}</p>
      {entries.length === 0 ? (
        <p className="text-xs text-gray-600">{emptyLabel}</p>
      ) : (
        <div className="flex gap-1 overflow-x-auto pb-1">
          {entries.map((entry) => (
            <CardThumb key={entry.id} entry={entry} qtyClass={qtyClass} />
          ))}
        </div>
      )}
    </div>
  );
}

function SidePlanSummary({
  title,
  accent,
  sideIn,
  sideOut,
}: {
  title: string;
  accent: "emerald" | "cyan";
  sideIn: DisplayEntry[];
  sideOut: DisplayEntry[];
}) {
  const titleClass = accent === "emerald" ? "text-emerald-300" : "text-cyan-300";
  return (
    <div className="rounded-xl border border-white/10 bg-black/20 p-2.5 sm:p-3">
      <p className={`mb-2 text-xs font-bold uppercase tracking-wide ${titleClass}`}>{title}</p>
      <div className="space-y-2">
        <CardRow
          label="Out"
          labelClass="text-rose-400/80"
          entries={sideOut}
          emptyLabel="—"
          qtyClass="bg-rose-600/90 text-white"
        />
        <CardRow
          label="In"
          labelClass="text-emerald-400/80"
          entries={sideIn}
          emptyLabel="—"
          qtyClass="bg-emerald-600/90 text-white"
        />
      </div>
    </div>
  );
}

function bumpSelection(entries: PrepSelection, cardId: string, delta: number, max: number): PrepSelection {
  const existing = entries.find((entry) => entry.cardId === cardId);
  const nextQty = Math.max(0, Math.min(max, (existing?.quantity ?? 0) + delta));

  if (!existing && nextQty <= 0) return entries;
  if (!existing) return [...entries, { cardId, quantity: nextQty }];
  if (nextQty === 0) return entries.filter((entry) => entry.cardId !== cardId);

  return entries.map((entry) => (entry.cardId === cardId ? { ...entry, quantity: nextQty } : entry));
}

function hydrateSavedEntries(entries: SavedDeckEntry[], cardsById: Map<string, CardRecord>) {
  return entries.flatMap((entry) => {
    const card = cardsById.get(entry.cardId);
    if (!card || entry.quantity <= 0) return [];
    return [{ card, quantity: entry.quantity }];
  });
}

function legendsFromCards(cards: CardRecord[]) {
  return cards
    .filter((card) => card.type === "Legend")
    .filter((card) => /[-,]/.test(card.name))
    .reduce<CardRecord[]>((acc, card) => {
      const key = baseLegendKey(card.name);
      if (!acc.some((item) => baseLegendKey(item.name) === key)) acc.push(card);
      return acc;
    }, [])
    .sort((a, b) => a.name.localeCompare(b.name));
}

export default function PrepBuilder() {
  const [decks, setDecks] = useState<SavedDeck[]>([]);
  const [cards, setCards] = useState<CardRecord[]>([]);
  const [inventory, setInventory] = useState<InventoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDeckId, setSelectedDeckId] = useState<string | null>(null);
  const [overviewDeckId, setOverviewDeckId] = useState<string | null>(null);
  const [savedPreps, setSavedPreps] = useState<SavedPrepPlan[]>([]);
  const [activePrepId, setActivePrepId] = useState<string | null>(null);
  const [opponentLegendId, setOpponentLegendId] = useState("");
  const [editOrder, setEditOrder] = useState<"first" | "second">("first");
  const [firstPlan, setFirstPlan] = useState<PrepSidePlan>(emptySidePlan);
  const [secondPlan, setSecondPlan] = useState<PrepSidePlan>(emptySidePlan);
  const [showImport, setShowImport] = useState(false);
  const [showGaps, setShowGaps] = useState(false);
  const [showGuides, setShowGuides] = useState(true);

  useEffect(() => {
    const refresh = () => {
      setDecks(listDecks());
      setSavedPreps(listPrepPlans());
      setInventory(listInventory());
    };
    refresh();
    fetch("/api/cards")
      .then((res) => res.json())
      .then((data: { cards: CardRecord[] }) => {
        const nextCards = data.cards ?? [];
        setCards(nextCards);
        if (migrateImportedPrepDecksToSaved(nextCards) > 0) refresh();
      })
      .catch(() => setCards([]))
      .finally(() => setLoading(false));
    window.addEventListener("rifthub:storage-sync", refresh);
    return () => window.removeEventListener("rifthub:storage-sync", refresh);
  }, []);

  const cardsById = useMemo(() => new Map(cards.map((card) => [card.id, card])), [cards]);
  const legends = useMemo(() => legendsFromCards(cards), [cards]);

  const prepDecks = useMemo(() => {
    return decks
      .map((deck): PrepDeckSummary => {
        const mainEntries = cards.length
          ? hydrateDeckEntries(deck, cards)
              .filter((entry) => entry.card.type !== "Rune" && entry.card.type !== "Battlefield")
              .map((entry) => ({
                id: entry.card.id,
                name: entry.card.name,
                quantity: entry.quantity,
                imageUrl: entry.card.imageUrl,
              }))
          : [];

        const sideboardEntries = hydrateSavedEntries(deck.sideboard ?? [], cardsById).map((entry) => ({
          id: entry.card.id,
          name: entry.card.name,
          quantity: entry.quantity,
          imageUrl: entry.card.imageUrl,
        }));

        return {
          id: deck.id,
          name: deck.name,
          subtitle: deck.legendName,
          imageUrl: cards.find((card) => card.id === deck.legendId)?.imageUrl ?? null,
          totalCards: deck.totalCards,
          sideboardCount: (deck.sideboard ?? []).reduce((sum, entry) => sum + entry.quantity, 0),
          battlefieldCount: deck.battlefieldIds.length,
          mainEntries,
          sideboardEntries,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [cards, cardsById, decks]);

  const selectedDeck = useMemo(
    () => prepDecks.find((deck) => deck.id === selectedDeckId) ?? null,
    [prepDecks, selectedDeckId],
  );

  const selectedSavedDeck = useMemo(
    () => decks.find((deck) => deck.id === selectedDeckId) ?? null,
    [decks, selectedDeckId],
  );

  const overviewSavedDeck = useMemo(
    () => decks.find((deck) => deck.id === overviewDeckId) ?? null,
    [decks, overviewDeckId],
  );

  const overviewDeck = useMemo(
    () => prepDecks.find((deck) => deck.id === overviewDeckId) ?? null,
    [prepDecks, overviewDeckId],
  );

  const prepsForDeck = useMemo(() => savedPreps.filter((prep) => prep.deckId === selectedDeckId), [savedPreps, selectedDeckId]);

  const overviewPreps = useMemo(
    () => savedPreps.filter((prep) => prep.deckId === overviewDeckId),
    [savedPreps, overviewDeckId],
  );

  const cardNamesById = useMemo(() => {
    const names = new Map<string, string>();
    for (const card of cards) names.set(card.id, card.name);
    for (const deck of prepDecks) {
      for (const entry of [...deck.mainEntries, ...deck.sideboardEntries]) {
        names.set(entry.id, entry.name);
      }
    }
    return names;
  }, [cards, prepDecks]);

  const cardImagesById = useMemo(() => {
    const images = new Map<string, string | null>();
    for (const card of cards) images.set(card.id, card.imageUrl);
    return images;
  }, [cards]);

  const opponentLegend = useMemo(
    () => legends.find((legend) => legend.id === opponentLegendId) ?? null,
    [legends, opponentLegendId],
  );

  const activePlan = editOrder === "first" ? firstPlan : secondPlan;
  const setActivePlan = editOrder === "first" ? setFirstPlan : setSecondPlan;
  const sideIn = activePlan.sideIn;
  const sideOut = activePlan.sideOut;

  const mainBaseCount = useMemo(
    () => selectedDeck?.mainEntries.reduce((sum, entry) => sum + entry.quantity, 0) ?? 0,
    [selectedDeck],
  );
  const sideInCount = useMemo(
    () => sideIn.reduce((sum, entry) => sum + entry.quantity, 0),
    [sideIn],
  );
  const sideOutCount = useMemo(
    () => sideOut.reduce((sum, entry) => sum + entry.quantity, 0),
    [sideOut],
  );
  const resultCount = mainBaseCount - sideOutCount + sideInCount;
  const isForty = resultCount === 40;

  function resetEditor() {
    setActivePrepId(null);
    setOpponentLegendId("");
    setEditOrder("first");
    setFirstPlan(emptySidePlan());
    setSecondPlan(emptySidePlan());
  }

  function selectDeck(deckId: string) {
    setOverviewDeckId(null);
    setSelectedDeckId(deckId);
    resetEditor();
  }

  function openOverview(deckId: string) {
    setSelectedDeckId(null);
    resetEditor();
    setOverviewDeckId(deckId);
  }

  function editPrepFromOverview(prep: SavedPrepPlan) {
    setOverviewDeckId(null);
    setSelectedDeckId(prep.deckId);
    loadPrep(prep);
  }

  function loadPrep(prep: SavedPrepPlan) {
    setActivePrepId(prep.id);
    setOpponentLegendId(prep.opponentLegendId);
    setEditOrder("first");
    setFirstPlan({
      sideIn: [...(prep.first?.sideIn ?? [])],
      sideOut: [...(prep.first?.sideOut ?? [])],
    });
    setSecondPlan({
      sideIn: [...(prep.second?.sideIn ?? [])],
      sideOut: [...(prep.second?.sideOut ?? [])],
    });
  }

  function bumpActiveOut(cardId: string, delta: number, max: number) {
    setActivePlan((prev) => ({
      ...prev,
      sideOut: bumpSelection(prev.sideOut, cardId, delta, max),
    }));
  }

  function bumpActiveIn(cardId: string, delta: number, max: number) {
    setActivePlan((prev) => ({
      ...prev,
      sideIn: bumpSelection(prev.sideIn, cardId, delta, max),
    }));
  }

  function applyGuidePlan(plan: PrepSidePlan) {
    setActivePlan({
      sideIn: plan.sideIn.map((e) => ({ ...e })),
      sideOut: plan.sideOut.map((e) => ({ ...e })),
    });
  }

  function saveCurrentPrep() {
    if (!selectedDeck) return;
    if (!opponentLegend) {
      window.alert("Choisis la légende adverse avant de sauvegarder.");
      return;
    }

    const mainBase = selectedDeck.mainEntries.reduce((sum, entry) => sum + entry.quantity, 0);
    const firstResult =
      mainBase -
      firstPlan.sideOut.reduce((s, e) => s + e.quantity, 0) +
      firstPlan.sideIn.reduce((s, e) => s + e.quantity, 0);
    const secondResult =
      mainBase -
      secondPlan.sideOut.reduce((s, e) => s + e.quantity, 0) +
      secondPlan.sideIn.reduce((s, e) => s + e.quantity, 0);

    if (firstResult !== 40 || secondResult !== 40) {
      window.alert(
        `Le deck doit faire exactement 40 cartes après side.\nGo 1st: ${firstResult}/40 · Go 2nd: ${secondResult}/40`,
      );
      return;
    }

    savePrepPlan({
      id: activePrepId ?? undefined,
      deckId: selectedDeck.id,
      deckName: selectedDeck.name,
      opponentLegendId: opponentLegend.id,
      opponentLegendName: opponentLegend.name,
      first: firstPlan,
      second: secondPlan,
    });

    setSavedPreps(listPrepPlans());
    resetEditor();
  }

  function removePrep(id: string) {
    if (!window.confirm("Supprimer cette prépa ?")) return;
    deletePrepPlan(id);
    setSavedPreps(listPrepPlans());
    if (activePrepId === id) resetEditor();
  }

  return (
    <div className="mx-auto flex h-[calc(100dvh-3rem)] w-full max-w-none flex-col overflow-hidden px-0 py-1 sm:h-[calc(100dvh-3.5rem)] sm:px-3 sm:py-2 lg:px-4">
      {!selectedDeck && (
        <div className="mb-3 flex shrink-0 flex-wrap items-end justify-between gap-3 px-3 sm:mb-4 sm:px-0">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold uppercase tracking-wider text-emerald-400/80">Sideboard</p>
            <h1 className="mt-1 text-2xl font-black sm:text-3xl">
              <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
                Préparation de side
              </span>
            </h1>
          </div>
          <button
            type="button"
            onClick={() => setShowImport((value) => !value)}
            className="rounded-2xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white hover:bg-white/10 sm:px-5 sm:py-3"
          >
            Importer un deck
          </button>
        </div>
      )}

      {showImport && !selectedDeck && (
        <div className="mb-3 shrink-0 px-3 sm:mb-4 sm:px-0">
          <DeckImportForm
            cards={cards}
            onCancel={() => setShowImport(false)}
            onImported={(deck) => {
              setDecks(listDecks());
              setShowImport(false);
              setSelectedDeckId(deck.id);
              resetEditor();
            }}
          />
        </div>
      )}

      {prepDecks.length === 0 ? (
        <div className="glass rounded-none border border-white/5 p-8 text-center text-gray-400 sm:rounded-2xl sm:p-12">
          Aucun deck pour le moment. Importe une liste ici, ou crée-en un dans Decks.
        </div>
      ) : (
        <>
          {!selectedDeck && !overviewDeck && (
            <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 overflow-y-auto px-3 sm:grid-cols-2 sm:px-0 lg:grid-cols-3 xl:grid-cols-4">
              {prepDecks.map((deck) => {
                const prepCount = savedPreps.filter((prep) => prep.deckId === deck.id).length;
                return (
                <div
                  key={deck.id}
                  className="glass flex min-h-[280px] flex-col rounded-2xl border border-white/5 p-3 text-left transition hover:border-emerald-400/40 hover:bg-white/8 sm:min-h-[320px] lg:min-h-[360px] lg:p-4"
                >
                  <button type="button" onClick={() => selectDeck(deck.id)} className="flex min-h-0 flex-1 flex-col text-left">
                    <div className="shrink-0">
                      <h2 className="truncate text-base font-bold sm:text-lg">{deck.name}</h2>
                      <p className="mt-1 truncate text-sm text-gray-400">{deck.subtitle}</p>
                    </div>
                    <div className="my-3 min-h-0 flex-1 overflow-hidden rounded-2xl border border-white/10 bg-black/20">
                      {deck.imageUrl ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img src={deck.imageUrl} alt={deck.subtitle} className="h-full w-full object-contain bg-black/30" />
                      ) : (
                        <div className="flex h-full items-center justify-center text-sm text-gray-600">Sans image</div>
                      )}
                    </div>
                  </button>
                  <div className="mt-auto flex shrink-0 flex-col gap-2">
                    <div className="flex flex-wrap gap-2 text-xs text-gray-400">
                      <span className="rounded-full bg-white/5 px-2 py-1">{deck.totalCards} cartes</span>
                      <span className="rounded-full bg-white/5 px-2 py-1">{deck.sideboardCount} sideboard</span>
                      <span className="rounded-full bg-white/5 px-2 py-1">
                        {prepCount} prepa{prepCount === 1 ? "" : "s"}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => openOverview(deck.id)}
                      className="w-full rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-3 py-2 text-sm font-medium text-emerald-200 hover:bg-emerald-500/20"
                    >
                      Voir toutes les prépas
                    </button>
                  </div>
                </div>
                );
              })}
            </div>
          )}

          {overviewDeck && !selectedDeck ? (
            <div className="glass flex min-h-0 flex-1 flex-col overflow-hidden rounded-none border-y border-white/5 sm:rounded-3xl sm:border sm:p-4">
              <div className="mb-3 flex shrink-0 flex-wrap items-start justify-between gap-3 px-3 sm:mb-4 sm:px-0">
                <div className="min-w-0 flex-1">
                  <button
                    type="button"
                    onClick={() => setOverviewDeckId(null)}
                    className="mb-2 flex items-center gap-1 text-base font-semibold text-gray-300 hover:text-white md:text-sm md:font-normal md:text-gray-400"
                  >
                    <span className="text-xl leading-none md:text-sm">←</span>
                    <span>Tous les decks</span>
                  </button>
                  <h2 className="truncate text-lg font-black sm:text-xl">{overviewDeck.name}</h2>
                  <p className="mt-1 text-sm text-gray-400">
                    {overviewPreps.length} prépa{overviewPreps.length === 1 ? "" : "s"} · {overviewDeck.subtitle}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => selectDeck(overviewDeck.id)}
                  className="rounded-xl bg-gradient-to-r from-emerald-600 to-cyan-600 px-4 py-2 text-sm font-bold"
                >
                  Nouvelle prépa
                </button>
              </div>

              {overviewSavedDeck && (
                <div className="mb-3 shrink-0 px-3 sm:px-0">
                  <MissingCardsPanel
                    deck={overviewSavedDeck}
                    inventory={inventory}
                    cards={cards}
                    compact
                  />
                </div>
              )}

              {overviewPreps.length === 0 ? (
                <div className="mx-3 flex flex-1 items-center justify-center rounded-2xl border border-dashed border-white/10 p-8 text-center text-gray-500 sm:mx-0">
                  Aucune prépa pour ce deck. Clique sur « Nouvelle prépa » pour commencer.
                </div>
              ) : (
                <div className="min-h-0 flex-1 space-y-3 overflow-y-auto sm:pr-1">
                  {overviewPreps.map((prep) => {
                    const firstIn = hydrateEntries(prep.first?.sideIn ?? [], cardNamesById, cardImagesById);
                    const firstOut = hydrateEntries(prep.first?.sideOut ?? [], cardNamesById, cardImagesById);
                    const secondIn = hydrateEntries(prep.second?.sideIn ?? [], cardNamesById, cardImagesById);
                    const secondOut = hydrateEntries(prep.second?.sideOut ?? [], cardNamesById, cardImagesById);
                    const legendImage =
                      cards.find((card) => card.id === prep.opponentLegendId)?.imageUrl ??
                      legends.find((legend) => legend.id === prep.opponentLegendId)?.imageUrl ??
                      null;

                    return (
                      <div
                        key={prep.id}
                        className="rounded-none border-y border-white/10 bg-black/25 p-3 sm:rounded-2xl sm:border sm:p-4"
                      >
                        <div className="mb-3 flex gap-3 sm:gap-4">
                          <div className="w-20 shrink-0 overflow-hidden rounded-xl border border-white/10 bg-black/40 sm:w-28">
                            {legendImage ? (
                              // eslint-disable-next-line @next/next/no-img-element
                              <img
                                src={legendImage}
                                alt={prep.opponentLegendName}
                                className="aspect-[5/7] w-full object-cover"
                              />
                            ) : (
                              <div className="flex aspect-[5/7] items-center justify-center text-xs text-gray-600">
                                ?
                              </div>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="truncate text-base font-bold sm:text-lg">
                              {prep.opponentLegendName}
                            </h3>
                            <p className="mt-0.5 text-xs text-gray-500">
                              Mis a jour {new Date(prep.updatedAt).toLocaleDateString("fr-FR")}
                            </p>
                            <div className="mt-2 flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => editPrepFromOverview(prep)}
                                className="rounded-xl border border-white/10 bg-white/5 px-3 py-1.5 text-sm hover:bg-white/10"
                              >
                                Modifier
                              </button>
                              <button
                                type="button"
                                onClick={() => removePrep(prep.id)}
                                className="rounded-xl px-3 py-1.5 text-sm text-red-300 hover:bg-red-500/10"
                              >
                                Supprimer
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className="grid gap-2 lg:grid-cols-2 lg:gap-3">
                          <SidePlanSummary title="Go 1st" accent="emerald" sideIn={firstIn} sideOut={firstOut} />
                          <SidePlanSummary title="Go 2nd" accent="cyan" sideIn={secondIn} sideOut={secondOut} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : null}

          {selectedDeck ? (
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden border-y border-white/5 bg-black/20 sm:rounded-2xl sm:border">
              {/* Ligne 1 mobile : retour + légende */}
              <div className="flex shrink-0 items-center gap-1.5 border-b border-white/5 px-2 py-1.5 md:hidden">
                <button
                  type="button"
                  onClick={() => setSelectedDeckId(null)}
                  aria-label="Retour"
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-2xl leading-none text-white hover:bg-white/10"
                >
                  ←
                </button>
                <select
                  value={opponentLegendId}
                  onChange={(event) => {
                    const nextId = event.target.value;
                    const existing = prepsForDeck.find((prep) => prep.opponentLegendId === nextId);
                    if (existing) {
                      loadPrep(existing);
                      return;
                    }
                    setActivePrepId(null);
                    setOpponentLegendId(nextId);
                    setEditOrder("first");
                    setFirstPlan(emptySidePlan());
                    setSecondPlan(emptySidePlan());
                  }}
                  className="min-w-0 flex-1 rounded-lg border border-white/10 bg-black/40 px-2 py-1.5 text-xs"
                >
                  <option value="">Légende adverse…</option>
                  {legends.map((legend) => (
                    <option key={legend.id} value={legend.id}>
                      {legend.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Ligne 2 mobile : compteur + 1st/2nd + Sauver */}
              <div className="flex shrink-0 items-center gap-1.5 border-b border-white/5 px-2 py-1.5 md:hidden">
                <div
                  className={`rounded-lg border px-2.5 py-1 text-xs font-bold tabular-nums ${
                    isForty
                      ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-300"
                      : "border-rose-500/40 bg-rose-500/15 text-rose-300"
                  }`}
                  title={`Base ${mainBaseCount} − Out ${sideOutCount} + In ${sideInCount}`}
                >
                  {resultCount}/40
                  {!isForty && <span className="ml-1 uppercase">invalide</span>}
                </div>
                <div className="flex rounded-lg border border-white/10 bg-black/30 p-0.5">
                  <button
                    type="button"
                    onClick={() => setEditOrder("first")}
                    disabled={!opponentLegendId}
                    className={`rounded-md px-2.5 py-1 text-xs font-semibold ${
                      editOrder === "first" ? "bg-emerald-500/25 text-white" : "text-gray-400"
                    } disabled:opacity-40`}
                  >
                    1st
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditOrder("second")}
                    disabled={!opponentLegendId}
                    className={`rounded-md px-2.5 py-1 text-xs font-semibold ${
                      editOrder === "second" ? "bg-cyan-500/25 text-white" : "text-gray-400"
                    } disabled:opacity-40`}
                  >
                    2nd
                  </button>
                </div>
                <button
                  type="button"
                  onClick={saveCurrentPrep}
                  disabled={!opponentLegend || !isForty}
                  className="ml-auto rounded-lg bg-gradient-to-r from-emerald-600 to-cyan-600 px-3 py-1.5 text-xs font-bold disabled:opacity-40"
                >
                  Sauver
                </button>
                <button
                  type="button"
                  onClick={() => setShowGaps((v) => !v)}
                  className={`rounded-lg border px-2 py-1 text-xs font-medium ${
                    showGaps
                      ? "border-amber-500/40 bg-amber-500/20 text-amber-100"
                      : "border-white/10 text-gray-400"
                  }`}
                >
                  Achats
                </button>
                <button
                  type="button"
                  onClick={() => setShowGuides((v) => !v)}
                  className={`rounded-lg border px-2 py-1 text-xs font-medium ${
                    showGuides
                      ? "border-emerald-500/40 bg-emerald-500/20 text-emerald-100"
                      : "border-white/10 text-gray-400"
                  }`}
                >
                  Guides
                </button>
              </div>
              <div className="hidden shrink-0 items-center gap-2 border-b border-white/5 px-3 py-1.5 md:flex">
                <button
                  type="button"
                  onClick={() => setSelectedDeckId(null)}
                  className="rounded-lg px-2 py-1 text-xs text-gray-400 hover:bg-white/5 hover:text-white"
                >
                  ←
                </button>
                <h2 className="max-w-[14rem] truncate text-sm font-black">{selectedDeck.name}</h2>
                <button
                  type="button"
                  onClick={() => setShowGaps((v) => !v)}
                  className={`rounded-lg border px-2 py-1 text-xs font-medium ${
                    showGaps
                      ? "border-amber-500/40 bg-amber-500/20 text-amber-100"
                      : "border-white/10 text-gray-400 hover:bg-white/5"
                  }`}
                >
                  Manquantes
                </button>
                <button
                  type="button"
                  onClick={() => setShowGuides((v) => !v)}
                  className={`rounded-lg border px-2 py-1 text-xs font-medium ${
                    showGuides
                      ? "border-emerald-500/40 bg-emerald-500/20 text-emerald-100"
                      : "border-white/10 text-gray-400 hover:bg-white/5"
                  }`}
                >
                  Guides
                </button>
                <select
                  value={opponentLegendId}
                  onChange={(event) => {
                    const nextId = event.target.value;
                    const existing = prepsForDeck.find((prep) => prep.opponentLegendId === nextId);
                    if (existing) {
                      loadPrep(existing);
                      return;
                    }
                    setActivePrepId(null);
                    setOpponentLegendId(nextId);
                    setEditOrder("first");
                    setFirstPlan(emptySidePlan());
                    setSecondPlan(emptySidePlan());
                  }}
                  className="min-w-0 flex-1 rounded-lg border border-white/10 bg-black/40 px-2 py-1.5 text-sm"
                >
                  <option value="">Légende adverse…</option>
                  {legends.map((legend) => (
                    <option key={legend.id} value={legend.id}>
                      {legend.name}
                    </option>
                  ))}
                </select>
                <div className="flex rounded-lg border border-white/10 bg-black/30 p-0.5">
                  <button
                    type="button"
                    onClick={() => setEditOrder("first")}
                    disabled={!opponentLegendId}
                    className={`rounded-md px-2.5 py-1 text-xs font-semibold ${
                      editOrder === "first" ? "bg-emerald-500/25 text-white" : "text-gray-400"
                    } disabled:opacity-40`}
                  >
                    1st
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditOrder("second")}
                    disabled={!opponentLegendId}
                    className={`rounded-md px-2.5 py-1 text-xs font-semibold ${
                      editOrder === "second" ? "bg-cyan-500/25 text-white" : "text-gray-400"
                    } disabled:opacity-40`}
                  >
                    2nd
                  </button>
                </div>
                <div
                  className={`rounded-lg border px-2.5 py-1 text-xs font-bold tabular-nums ${
                    isForty
                      ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-300"
                      : "border-rose-500/40 bg-rose-500/15 text-rose-300"
                  }`}
                  title={`Base ${mainBaseCount} − Out ${sideOutCount} + In ${sideInCount}`}
                >
                  {resultCount}/40
                  {!isForty && <span className="ml-1 uppercase">invalide</span>}
                </div>
                <select
                  value={activePrepId ?? ""}
                  onChange={(event) => {
                    const id = event.target.value;
                    if (!id) {
                      resetEditor();
                      return;
                    }
                    const prep = prepsForDeck.find((p) => p.id === id);
                    if (prep) loadPrep(prep);
                  }}
                  className="max-w-[10rem] rounded-lg border border-white/10 bg-black/40 px-2 py-1.5 text-xs lg:max-w-[14rem]"
                >
                  <option value="">Nouvelle prépa</option>
                  {prepsForDeck.map((prep) => (
                    <option key={prep.id} value={prep.id}>
                      {prep.opponentLegendName}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={saveCurrentPrep}
                  disabled={!opponentLegend || !isForty}
                  className="ml-auto rounded-lg bg-gradient-to-r from-emerald-600 to-cyan-600 px-3 py-1.5 text-xs font-bold disabled:opacity-40"
                >
                  Sauver
                </button>
              </div>

              {showGaps && selectedSavedDeck && (
                <div className="shrink-0 border-b border-white/5 p-2 sm:p-3">
                  <MissingCardsPanel
                    deck={selectedSavedDeck}
                    inventory={inventory}
                    cards={cards}
                    compact
                  />
                </div>
              )}

              {showGuides && selectedSavedDeck && (
                <div className="max-h-[42%] min-h-[9rem] shrink-0 overflow-hidden border-b border-white/5 sm:max-h-[38%]">
                  <SideGuidePanel
                    deck={selectedSavedDeck}
                    cards={cards}
                    opponentLegend={opponentLegend}
                    playOrder={editOrder}
                    onApply={(plan) => applyGuidePlan(plan)}
                  />
                </div>
              )}

              <div
                className={`flex min-h-0 flex-1 flex-col p-1.5 sm:p-2 ${
                  opponentLegendId ? "" : "pointer-events-none opacity-40"
                }`}
              >
                <PrepSideEditor
                  mainEntries={selectedDeck.mainEntries}
                  sideboardEntries={selectedDeck.sideboardEntries}
                  sideIn={sideIn}
                  sideOut={sideOut}
                  onBumpIn={bumpActiveIn}
                  onBumpOut={bumpActiveOut}
                />
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
